"use client"

import { useState, useMemo } from "react"
import { Plus, Trash2, RotateCcw } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn } from "./formato"

interface Ingredient {
  id: number
  nombre: string
  cantidad: number
  unidad: string
  precioPorUnidad: number
}

const T = {
  es: {
    ingredientes: [
      "Lechuga romana",
      "Pechuga de pollo",
      "Pan tostado (croutons)",
      "Parmesano",
      "Salsa César",
    ],
    plato: "Ensalada César",
    datosPlato: "Datos del plato",
    limpiar: "Limpiar",
    nombrePlato: "Nombre del plato (opcional)",
    placeholderPlato: "Ej: Ensalada César",
    tituloIngredientes: "Ingredientes",
    ingrediente: "Ingrediente",
    cantidad: "Cant.",
    unidades: "uds",
    precioUd: "€/ud",
    eliminar: "Eliminar ingrediente",
    anadir: "Añadir ingrediente",
    precioVenta: "Precio de venta (€)",
    raciones: "Nº de raciones",
    resultados: "Resultados",
    ideal: "El food cost ideal en restauración está entre el 25% y el 35%",
    costeTotal: "Coste total",
    costeRacion: "Coste / ración",
    margenBruto: "Margen bruto",
    margenBrutoPct: "Margen bruto %",
  },
  en: {
    ingredientes: [
      "Romaine lettuce",
      "Chicken breast",
      "Toasted bread (croutons)",
      "Parmesan",
      "Caesar dressing",
    ],
    plato: "Caesar salad",
    datosPlato: "Dish details",
    limpiar: "Reset",
    nombrePlato: "Dish name (optional)",
    placeholderPlato: "e.g. Caesar salad",
    tituloIngredientes: "Ingredients",
    ingrediente: "Ingredient",
    cantidad: "Qty",
    unidades: "units",
    precioUd: "€/unit",
    eliminar: "Remove ingredient",
    anadir: "Add ingredient",
    precioVenta: "Selling price (€)",
    raciones: "No. of portions",
    resultados: "Results",
    ideal: "In restaurants, a healthy food cost sits between 25% and 35%",
    costeTotal: "Total cost",
    costeRacion: "Cost per portion",
    margenBruto: "Gross margin",
    margenBrutoPct: "Gross margin %",
  },
}

const ingredientesBase: Omit<Ingredient, "nombre">[] = [
  { id: 1, cantidad: 200, unidad: "g", precioPorUnidad: 0.003 },
  { id: 2, cantidad: 150, unidad: "g", precioPorUnidad: 0.008 },
  { id: 3, cantidad: 30, unidad: "g", precioPorUnidad: 0.005 },
  { id: 4, cantidad: 20, unidad: "g", precioPorUnidad: 0.018 },
  { id: 5, cantidad: 40, unidad: "ml", precioPorUnidad: 0.012 },
]

const defaultIngredientsDe = (idioma: Idioma): Ingredient[] =>
  ingredientesBase.map((ing, i) => ({ ...ing, nombre: T[idioma].ingredientes[i] }))

const defaultState = {
  precioVenta: 12.5,
  raciones: 1,
}

let nextId = 100

export default function FoodCostCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const eur = (n: number) => (idioma === "en" ? eurosEn(n) : `${n.toFixed(2)}€`)
  const [nombrePlato, setNombrePlato] = useState(t.plato)
  const [ingredients, setIngredients] = useState<Ingredient[]>(() => defaultIngredientsDe(idioma))
  const [precioVenta, setPrecioVenta] = useState(defaultState.precioVenta)
  const [raciones, setRaciones] = useState(defaultState.raciones)

  const results = useMemo(() => {
    const costeTotal = ingredients.reduce(
      (sum, ing) => sum + ing.cantidad * ing.precioPorUnidad,
      0
    )
    const costePorRacion = raciones > 0 ? costeTotal / raciones : 0
    const foodCostPct =
      precioVenta > 0 ? (costePorRacion / precioVenta) * 100 : 0
    const margenBruto = precioVenta - costePorRacion
    const margenBrutoPct = 100 - foodCostPct

    return { costeTotal, costePorRacion, foodCostPct, margenBruto, margenBrutoPct }
  }, [ingredients, precioVenta, raciones])

  const addIngredient = () => {
    setIngredients([
      ...ingredients,
      { id: nextId++, nombre: "", cantidad: 0, unidad: "g", precioPorUnidad: 0 },
    ])
  }

  const removeIngredient = (id: number) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((i) => i.id !== id))
    }
  }

  const updateIngredient = (id: number, field: keyof Ingredient, value: string | number) => {
    setIngredients(
      ingredients.map((i) => (i.id === id ? { ...i, [field]: value } : i))
    )
  }

  const reset = () => {
    setNombrePlato(t.plato)
    setIngredients(defaultIngredientsDe(idioma))
    setPrecioVenta(defaultState.precioVenta)
    setRaciones(defaultState.raciones)
  }

  const getFoodCostColor = (pct: number) => {
    if (pct <= 0) return "text-zinc-400"
    if (pct < 25) return "text-emerald-600"
    if (pct <= 30) return "text-lime-600"
    if (pct <= 35) return "text-amber-600"
    return "text-red-600"
  }

  const getFoodCostBg = (pct: number) => {
    if (pct <= 0) return "bg-zinc-100"
    if (pct < 25) return "bg-emerald-50"
    if (pct <= 30) return "bg-lime-50"
    if (pct <= 35) return "bg-amber-50"
    return "bg-red-50"
  }

  const getBarColor = (pct: number) => {
    if (pct < 25) return "bg-emerald-500"
    if (pct <= 30) return "bg-lime-500"
    if (pct <= 35) return "bg-amber-500"
    return "bg-red-500"
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.datosPlato}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          {/* Nombre del plato */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.nombrePlato}
            </label>
            <input
              type="text"
              value={nombrePlato}
              onChange={(e) => setNombrePlato(e.target.value)}
              placeholder={t.placeholderPlato}
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent transition-shadow"
            />
          </div>

          {/* Ingredientes */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-3">
              {t.tituloIngredientes}
            </label>
            <div className="space-y-3">
              {ingredients.map((ing) => (
                <div
                  key={ing.id}
                  className="grid grid-cols-12 gap-2 items-start"
                >
                  <div className="col-span-12 sm:col-span-4">
                    <input
                      type="text"
                      value={ing.nombre}
                      onChange={(e) =>
                        updateIngredient(ing.id, "nombre", e.target.value)
                      }
                      placeholder={t.ingrediente}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <input
                      type="number"
                      value={ing.cantidad || ""}
                      onChange={(e) =>
                        updateIngredient(
                          ing.id,
                          "cantidad",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      placeholder={t.cantidad}
                      min="0"
                      step="any"
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <select
                      value={ing.unidad}
                      onChange={(e) =>
                        updateIngredient(ing.id, "unidad", e.target.value)
                      }
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                    >
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="ml">ml</option>
                      <option value="L">L</option>
                      <option value="uds">{t.unidades}</option>
                    </select>
                  </div>
                  <div className="col-span-4 sm:col-span-3">
                    <div className="relative">
                      <input
                        type="number"
                        value={ing.precioPorUnidad || ""}
                        onChange={(e) =>
                          updateIngredient(
                            ing.id,
                            "precioPorUnidad",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder={t.precioUd}
                        min="0"
                        step="any"
                        className="w-full px-3 py-2 pr-8 rounded-xl border border-zinc-200 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400">
                        €
                      </span>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <button
                      onClick={() => removeIngredient(ing.id)}
                      className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                      title={t.eliminar}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={addIngredient}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-lime-600 hover:text-lime-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t.anadir}
            </button>
          </div>

          {/* PVP y raciones */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {t.precioVenta}
              </label>
              <input
                type="number"
                value={precioVenta || ""}
                onChange={(e) => setPrecioVenta(parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                min="0"
                step="any"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {t.raciones}
              </label>
              <input
                type="number"
                value={raciones || ""}
                onChange={(e) => setRaciones(parseInt(e.target.value) || 0)}
                placeholder="1"
                min="1"
                step="1"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultados}</h2>

          {/* Big food cost */}
          <div
            className={`rounded-2xl p-6 text-center ${getFoodCostBg(results.foodCostPct)}`}
          >
            <p className="text-sm font-medium text-zinc-600 mb-1">Food Cost</p>
            <p
              className={`text-5xl font-bold ${getFoodCostColor(results.foodCostPct)}`}
            >
              {results.foodCostPct > 0 ? results.foodCostPct.toFixed(1) : "0.0"}%
            </p>
            {nombrePlato && (
              <p className="text-sm text-zinc-500 mt-2">{nombrePlato}</p>
            )}
          </div>

          {/* Visual bar */}
          <div>
            <div className="flex justify-between text-xs text-zinc-500 mb-1.5">
              <span>0%</span>
              <span>25%</span>
              <span>30%</span>
              <span>35%</span>
              <span>50%+</span>
            </div>
            <div className="relative h-4 rounded-full bg-zinc-200 overflow-hidden">
              {/* Zone markers */}
              <div className="absolute inset-0 flex">
                <div className="w-[50%] bg-emerald-200" />
                <div className="w-[10%] bg-lime-200" />
                <div className="w-[10%] bg-amber-200" />
                <div className="w-[30%] bg-red-200" />
              </div>
              {/* Indicator */}
              {results.foodCostPct > 0 && (
                <div
                  className={`absolute top-0 bottom-0 w-1.5 rounded-full ${getBarColor(results.foodCostPct)} shadow-sm transition-all duration-300`}
                  style={{
                    left: `${Math.min(results.foodCostPct, 50) * 2}%`,
                  }}
                />
              )}
            </div>
            <p className="text-xs text-zinc-500 mt-2 text-center">
              {t.ideal}
            </p>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.costeTotal}</p>
              <p className="text-xl font-bold text-zinc-900">
                {eur(results.costeTotal)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.costeRacion}</p>
              <p className="text-xl font-bold text-zinc-900">
                {eur(results.costePorRacion)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.margenBruto}</p>
              <p
                className={`text-xl font-bold ${results.margenBruto >= 0 ? "text-emerald-600" : "text-red-600"}`}
              >
                {eur(results.margenBruto)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.margenBrutoPct}</p>
              <p
                className={`text-xl font-bold ${results.margenBrutoPct >= 0 ? "text-emerald-600" : "text-red-600"}`}
              >
                {results.margenBrutoPct.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
